import { useState } from "react";
import {
  Box,
  TableRow,
  TableCell,
  DialogContentText,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  FormGroup,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../app/hooks";

import LoadingSpinner from "../common/Loading";
import { StyledTableCell } from "../common/styles";

import AppSnackbar from "../common/AppSnackbar";
import AppConfirmationDailog from "../common/AppConfirmationDialog";
import "../../index.css";
import AppErrorMessage from "../common/AppErrorMessage";
import { AuthRole } from "../../utils/roles";
import { getAllComponents } from "../../features/components/componentSlice";
import { addPermissions, deleteClinicStaff, deletePermissions, getStaffPermissions } from "../../features/staff/staffSlice";

interface IProps {
  staff: any;
  clinicId: string;
  staff_id: string;
  staffPermession: any;
  getStaff: () => void;
}

const StaffPermissions = ({
  staff,
  clinicId,
  staff_id,
  staffPermession,
  getStaff,
}: IProps) => {
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const dispatch = useAppDispatch();
  const {
    isPermissionsLoading,
    permissionsPayload: { data: staffPermissions },
    isAddPermissionsLoading,
    addPermissionsMessage,
    isAddPermissionsError,
  }: any = useAppSelector((state) => state.staff);
  const { role }: any = AuthRole();
  const { isAllComponentsLoading: componentsLoading }: any = useAppSelector(
    (state) => state.components
  );

  const { isDeleteClinicStaffLoading }: any = useAppSelector(
    (state) => state.clinic
  );

  const handleDelete = (module_name: string, submodule: string) => {
    if (role !== "staff") {
      const obj = {
        staff_id: staff_id,
        clinic_id: clinicId!,
        permission: {
          module: module_name,
          submodule: submodule,
        },
      };
      dispatch(deletePermissions(obj)).then((res) => {
        if (
          res.meta.requestStatus === "fulfilled" &&
          res.payload.success === true
        ) {
          getStaff();
        }
      });
    }
  };

  const handleSubmit = (module_name: string, submodule: string) => {
    if (role !== "staff") {
      const obj = {
        staff_id: staff_id,
        clinic_id: clinicId!,
        permission: {
          module: module_name,
          submodule: submodule,
        },
      };

      dispatch(addPermissions(obj))?.then((res) => {
        if (res.payload.success === true) {
          const obj = {
            staff_id: staff_id,
            clinic_id: clinicId!,
          };
          getStaff();
          dispatch(getAllComponents());
          dispatch(getStaffPermissions(obj));
        }
      });
    }
  };

  const handleDeleteStaff = (sId: string) => {
    const obj = {
      clinic_id: clinicId,
      staff_id: sId,
    };
    dispatch(deleteClinicStaff(obj))?.then((res:any) => {
      if (res?.payload?.success === true) {
        setOpenSnackbar(true);
      }
    });

  };

  const displayTablePermissions = (
    column: string,
    module_name: string,
    permissions: Array<any>
  ) => {
    const exists = permissions?.some((item) => {
      return column == item?.submodule;
    });

    if (exists) {
      const isChecked = staffPermession?.some((item: any) => {
        if (item?.module_name === module_name) {
          return item?.array_agg?.some((item2: any) => {
            return item2?.submodule === column;
          });
        } else {
          return false;
        }
      });

      return (
        <StyledTableCell sx={{ pl: 5 }}>
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  style={{
                    borderColor: "black",
                    paddingLeft: "10px",
                    color: isChecked ? "#3686ec" : "black",
                  }}
                  checked={isChecked}
                />
              }
              label=""
              onChange={(e: any) => {
                if (e.target.checked === true) {
                  handleSubmit(module_name, column);
                } else {
                  handleDelete(module_name, column);
                }
              }}
            />
          </FormGroup>
        </StyledTableCell>
      );
    } else {
      return <StyledTableCell></StyledTableCell>;
    }
  };

  return (
    <>
      <TableRow>
        <TableCell
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
          colSpan={7}
        >
          {isPermissionsLoading ||
          componentsLoading ||
          isAddPermissionsLoading ? (
            <LoadingSpinner style={{ minHeight: "30vh" }} />
          ) : (
            <Box
              sx={{
                margin: 1,
                pb: 5,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
              }}
            >
              <TableContainer>
                <Box className="table-width">
                  <Table
                    sx={{
                      "& .MuiTableCell-root": {
                        borderLeft: "1px solid rgba(224, 224, 224, 1)",
                      },
                      border: "1px solid rgba(224, 224, 224, 1)",
                    }}
                  >
                    <TableHead>
                      <TableRow>
                        <StyledTableCell>Permissions </StyledTableCell>
                        <StyledTableCell>View</StyledTableCell>
                        <StyledTableCell>Edit</StyledTableCell>
                        <StyledTableCell>Add</StyledTableCell>
                        <StyledTableCell>Delete</StyledTableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {staff?.data?.map((item: any, index: number) => {
                        if (item.permissions?.length < 1) {
                          return <></>;
                        }

                        return (
                          <TableRow>
                            <StyledTableCell sx={{ fontWeight: "bold" }}>
                              {item?.module_name}
                            </StyledTableCell>
                            {displayTablePermissions(
                              "view",
                              item?.module_name,
                              item?.permissions
                            )}
                            {displayTablePermissions(
                              "edit",
                              item.module_name,
                              item.permissions
                            )}
                            {displayTablePermissions(
                              "add",
                              item.module_name,
                              item.permissions
                            )}
                            {displayTablePermissions(
                              "delete",
                              item.module_name,
                              item.permissions
                            )}
                          </TableRow>
                        );
                      })}
                    </TableBody>
                    <Box sx={{ width: "100%", mt: 3 }}>
                      {isAddPermissionsError && (
                        <AppErrorMessage message={addPermissionsMessage} />
                      )}
                    </Box>
                  </Table>
                </Box>
              </TableContainer>
            </Box>
          )}
        </TableCell>
      </TableRow>

      <AppSnackbar
        message={"Success"}
        open={openSnackbar}
        handleClose={() => setOpenSnackbar(false)}
      />
      <AppConfirmationDailog
        title="Delete"
        open={dialogOpen}
        handleClose={() => setDialogOpen(false)}
        handleConfirm={() => handleDeleteStaff(staff.id)}
        isLoading={isDeleteClinicStaffLoading}
      >
        <DialogContentText
          id="alert-dialog-doctor"
          sx={{
            p: 2,
            width: 300,
          }}
        >
          Are you sure you want to delete?
        </DialogContentText>
      </AppConfirmationDailog>
    </>
  );
};
export default StaffPermissions;
